/**
 * cells/EvolutionCell.tsx
 *
 * Renders the EPC history timeline with trend icon.
 * Each badge is a tooltip with actions (open file, edit, delete).
 */
import { Tooltip as HeroTooltip } from "@heroui/react";
import { TrendUp, TrendDown, ArrowRight, Trash } from "@phosphor-icons/react";
import {
  EPCData,
  getEPCColor,
  getDisplayLabel,
} from "shared/components/maintenance/hooks/useEnergyPerformanceData";
import { getImprovementTrend, isSpecificEPCIncomplete, formatDate } from "./utils";

interface EvolutionCellProps {
  unit: EPCData;
  onEdit: (fileLogId: number, unit: EPCData) => void;
  onDelete: (
    fileLogId: number,
    unitLocation: string,
    epcLabel?: string,
    documentDate?: string,
    kWh?: string,
  ) => void;
}

function EpcHistoryEntry({
  epc,
  unit,
  onEdit,
  onDelete,
}: {
  epc: any;
  unit: EPCData;
  onEdit: (fileLogId: number, unit: EPCData) => void;
  onDelete: (
    fileLogId: number,
    unitLocation: string,
    epcLabel?: string,
    documentDate?: string,
    kWh?: string,
  ) => void;
}) {
  const isIncomplete = isSpecificEPCIncomplete(epc);
  const hasFileUrl = epc.fileLogId && epc.sharePointFileUrl;

  const tooltipContent = (
    <div className="flex flex-col gap-2 p-2">
      <div className="text-sm font-medium">
        {epc.label} - {formatDate(epc.documentDate)}
      </div>
      {epc.kWh && (
        <div className="text-xs text-gray-600">{epc.kWh} kWh/m²/jaar</div>
      )}
      <div className="mt-2 flex gap-2">
        {hasFileUrl && (
          <a
            href={epc.sharePointFileUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="bg-moss-primary hover:bg-moss-primary/90 rounded-sm px-2 py-1 text-xs text-white transition-colors"
          >
            Open EPC
          </a>
        )}
        {epc.fileLogId && (
          <button
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onEdit(epc.fileLogId, unit);
            }}
            className={`rounded px-2 py-1 text-xs transition-colors ${
              isIncomplete
                ? "bg-yellow-500 text-white hover:bg-yellow-600"
                : "bg-neutral-primary hover:bg-neutral-primary/90 text-white"
            }`}
          >
            {isIncomplete ? "EPC data aanvullen" : "Bewerk EPC data"}
          </button>
        )}
        {epc.fileLogId && (
          <button
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onDelete(
                epc.fileLogId,
                unit.location,
                epc.label,
                epc.documentDate,
                epc.kWh,
              );
            }}
            className="flex items-center gap-1 rounded-sm bg-red-500 px-2 py-1 text-xs text-white transition-colors hover:bg-red-600"
          >
            <Trash size={12} />
            Verwijder EPC
          </button>
        )}
      </div>
    </div>
  );

  return (
    <HeroTooltip
      content={tooltipContent}
      placement="top"
      classNames={{
        content: "p-0 bg-white border border-neutral-primary/20 shadow-lg",
      }}
    >
      <span
        className={`flex h-6 w-6 cursor-pointer items-center justify-center rounded text-xs font-bold transition-opacity hover:opacity-80 ${getEPCColor(epc.label)} ${
          isIncomplete ? "ring-2 ring-yellow-400" : ""
        }`}
      >
        {getDisplayLabel(epc.label)}
      </span>
    </HeroTooltip>
  );
}

function TrendIcon({
  trend,
  history,
}: {
  trend: "improved" | "declined" | "unchanged";
  history: any[];
}) {
  const oldest = history[0];
  const newest = history[history.length - 1];
  const oldDate = formatDate(oldest.documentDate);
  const newDate = formatDate(newest.documentDate);

  const tooltipText =
    trend === "improved"
      ? `EPC verbeterd van ${oldest.label} (${oldDate}) naar ${newest.label} (${newDate})`
      : trend === "declined"
        ? `EPC verslechterd van ${oldest.label} (${oldDate}) naar ${newest.label} (${newDate})`
        : `EPC ongewijzigd: ${oldest.label} (${oldDate}) naar ${newest.label} (${newDate})`;

  const Icon =
    trend === "improved"
      ? TrendUp
      : trend === "declined"
        ? TrendDown
        : ArrowRight;
  const iconClass =
    trend === "improved"
      ? "text-moss-primary"
      : trend === "declined"
        ? "text-brick-primary"
        : "text-neutral-secondary-mid";

  return (
    <HeroTooltip content={tooltipText}>
      <Icon size={16} className={`${iconClass} cursor-help`} />
    </HeroTooltip>
  );
}

export function EvolutionCell({ unit, onEdit, onDelete }: EvolutionCellProps) {
  const trend = getImprovementTrend(unit.epcHistory);

  if (unit.epcHistory.length <= 1) {
    return (
      <span className="text-neutral-secondary-mid text-sm italic">
        Geen historiek
      </span>
    );
  }

  const sortedHistory = [...unit.epcHistory].sort((a, b) => {
    if (!a.documentDate && !b.documentDate) return 0;
    if (!a.documentDate) return 1;
    if (!b.documentDate) return -1;
    return (
      new Date(a.documentDate).getTime() - new Date(b.documentDate).getTime()
    );
  });

  return (
    <div className="flex items-center gap-2">
      <div className="flex gap-1">
        {sortedHistory.map((epc, index) => (
          <EpcHistoryEntry
            key={index}
            epc={epc}
            unit={unit}
            onEdit={onEdit}
            onDelete={onDelete}
          />
        ))}
      </div>
      {trend && <TrendIcon trend={trend} history={sortedHistory} />}
    </div>
  );
}
